<template>
    <div>
        <p class="headline">
            <v-icon style="margin-top:-5px;">fa-microphone</v-icon> Manage
            <small>messages</small>
        </p>

        <v-breadcrumbs :items="items">
            <template v-slot:item="props">
                <v-icon v-if="props.item.icon" style="color:#fff;">{{props.item.icon}}</v-icon>
                <v-breadcrumbs-item
                @click.prevent="props.item.href"
                :class="[props.item.disabled && 'disabled']"
                >{{ props.item.text.toUpperCase() }}</v-breadcrumbs-item>
            </template>
        </v-breadcrumbs>
      
        <v-card tile class="mt-4">
            <v-card-title>Messages
                <v-spacer></v-spacer>
                <v-text-field
                    class="mr-4"
                    style="max-width: 350px;"
                    dense
                    v-model="search"
                    append-icon="mdi-magnify"
                    label="Search"
                    single-line
                    hide-details
                ></v-text-field>
            <v-btn  color="primary ma-3" tile @click.stop="newMessage">Upload Message</v-btn>
            </v-card-title>
            
            <v-data-table
                dense
                :search="search"
                :headers="headers"
                :items="messages"
                :items-per-page="20"
                item-key="id"
                class="elevation-1"
                :footer-props="{
                  itemsPerPageOptions:[10,20,30,50,100,-1]
                }"
                >
                <template v-slot:[`item.actions`]="{ item }">
                    <audio :id="'player'+item.id">
                        <source :src="item.uploaded_file" type="audio/mpeg">
                        Your browser does not support the audio element.
                    </audio>
                    <v-icon
                        v-if="item.play"
                        :disabled="item.file_ext == 'aiff'"
                        small
                        class="mr-2"
                        @click="playAudio(item)"
                        >play_arrow
                    </v-icon>

                    <v-icon
                        small 
                        v-else
                        class="mr-2"
                        @click="pauseAudio(item)">
                        pause
                    </v-icon>

                    <v-icon
                        small
                        class="mr-2"
                        @click="editItem(item)">
                        edit
                    </v-icon>

                    <v-icon
                        small
                        class="mr-2"
                        @click="deleteItem(item)">
                        delete
                    </v-icon>

                    <v-icon
                        small
                        @click="download(item.uploaded_file, item.stored_file_name, item.file_ext, item.file_size)">
                        cloud_download
                    </v-icon>
                </template>
                
                <template v-slot:[`item.original_name`]="{item}">
                    <v-tooltip top text>
                    <template v-slot:activator="{ on }">
                        <span
                        v-on="on"
                        >{{item.stored_file_name}}</span>
                    </template>
                    <span>{{item.uploaded_file}}</span>
                    </v-tooltip>
                </template>
                
                <template v-slot:[`item.file_size`]="{item}">
                    {{(item.file_size / 1000000).toFixed(2)}} MB
                </template>
                <template v-slot:[`item.av_duration`]="{item}">
                    {{getFormatedDuration(item.av_duration)}}
                </template>
                <template v-slot:[`item.created_at`]="{ item }">
                    {{ formatedDate(item.created_at) }}
                </template>

            </v-data-table>
        </v-card>

        <v-dialog v-model="dialog" persistent max-width="500">
            <v-card tile>
                <v-card-title class="pl-8" v-if="!isUploading">{{dialogTitle}}</v-card-title>
                <v-card-text>
                    <v-form ref="messageForm">
                        <v-col v-if="editedIndex == -1">   
                            <p style="font-weight: bold;">Upload file type: MP3, AAC, M4A are the only upload file types accepted. Max storage per user login: 150 files, 2 MB per file</p>
                        <input type="file" ref="uploadedFile" accept="audio/*" style="width:100%;" class="mb-4" @change="onFileChange"/>
                        <p style="color:red;" v-if="invalidFormat">File format not supported</p>
                        <p style="color:red;" v-if="sizeToBig">Max file size exceeded.</p>
                        </v-col>
                        <v-col>
                            <v-text-field 
                                label="Description"
                                :rules="[
                                (v) => !!v || 'This field is required.'
                                ]"
                                dense
                                v-model="editedItem.stored_file_name"
                                prepend-icon="attachment"
                                ></v-text-field>
                        </v-col>
                        <v-col>
                            <v-text-field 
                                label="Uploaded by"
                                dense
                                :rules="[
                                (v)=> !!v || 'This field is required.'
                                ]"
                                v-model="editedItem.uploaded_by"
                                prepend-icon="person"
                                readonly
                                ></v-text-field>
                        </v-col>
                         <v-col>
                           <p class="mb-0">By uploading this message, you confirm that you are the copyright owner. The SiriusXM license does not cover music used in messages</p>
                        </v-col>
                    </v-form>

                    <v-row
                        v-if="isUploading"
                        class="fill-height"
                        align-content="center"
                        justify="center">
                        <v-col
                        class="subtitle-1 text-center"
                        cols="12">
                        Your message is uploading...
                        </v-col>
                        <v-col cols="6">
                        <v-progress-linear
                            color="primary accent-4"
                            indeterminate
                            rounded
                            height="6"
                        ></v-progress-linear>
                        </v-col>
                    </v-row>

                </v-card-text>
                <v-card-actions v-if="!isUploading">
                    <v-spacer></v-spacer>
                    <v-btn color="warning" @click="close" tile>Close</v-btn>
                    <v-btn color="primary" tile class="mr-4" @click.prevent="save" :disabled="isValidExt">{{btnText}}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog 
            max-width="400"
            v-model="deleteDialog">
            <v-card tile>
                <v-card-title>Are you sure?</v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pa-6">
                    <p class="title">Do you want to delete this message?</p>
                    <div v-if="shouldDeleteItem" class="user-info">
                        <p class="mb-1">Description: {{shouldDeleteItem.stored_file_name}}</p>
                    </div>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" @click.prevent="deleteDialogClose" tile >No</v-btn>
                    <v-btn color="red accent-4" @click.prevent="confirmDelete" tile class="white--text">Yes</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>
<script>
import axios from './axios'
import moment from 'moment-timezone'
import momentf from 'moment'
export default {
    data(){
        return {
            btnText: 'Upload Message',
            player:null,
            retriver: null,
            showPlayButton : true,
            isValidExt:true,
            invalidFormat:false,
            sizeToBig: false,
            fileName:'',
            isUploading: false,
            workingAsAdmin: true,
            search: null,
            messages: [],
            allMessages: [],
            dialog: false,
            fileSize: 0,
            dialogTitle: 'Add Message',
            items: [
                {
                text: "Messages",
                disabled: true,
                href: '#',
                icon:'fa-microphone'
                }
            ],
            editedItem:{
                id: 0,
                uploaded_file: '',
                stored_file_name: '',
                uploaded_by: this.$store.getters.getName,
                created_at: '',
                updated_at: ''
            },
            defaultItem:{
                id: 0,
                uploaded_file: '',
                stored_file_name: '',
                uploaded_by: this.$store.getters.getName,
                created_at: '',
                updated_at: ''
            },
            editedIndex: -1,
            deleteDialog: false,
            isConfirmDelete: false,
            shouldDeleteItem: null
        }
    },
    methods:{
        formatedDate(date){
            return momentf(date).format('MM-DD-YYYY')
        },
        getNameWithOutExt(name){
            return name.split('.')[0]
        },
        playAudio(item){

            let index = this.messages.indexOf(item)
            this.messages[index].play = 0
            this.player = document.getElementById('player'+item.id)
            if(this.retriver){
                clearInterval(this.retriver)
            }

            this.retriver = setInterval(()=>{
                if(this.player.paused){
                    this.messages[index].play = 1
                    clearInterval(this.retriver)
                }
            }, 1000)
            
            this.player.play() 
        },
        pauseAudio(item){
            let index = this.messages.indexOf(item)
            
            if(this.retriver){
                clearInterval(this.retriver)
            }

            if(!this.player.paused){
                this.player.pause()
                this.messages[index].play = 1
            }

        },
        getFormatedDuration(item){
            var min = item.split(".")[0]
            var sec = item.split(".")[1]
            return min +' min ' + sec.substring(0,2) + ' secs'
        },
        selectRow(item){
            this.selected=[]
            this.selected.push(item)
            this.onPlaylistChange(item.id)
        },
        openFileChoooser(){
            this.fileChooser = true
        },
        onFileChange(){
            const file = this.$refs.uploadedFile.files[0]
            this.editedItem.uploaded_file = URL.createObjectURL(file);
            let validExt = ['mp3', 'aac', 'm4a']
            let fileExtention = file.name.split('.').pop()

            let des = file.name.replace("."+fileExtention, "")
            this.editedItem.stored_file_name = des

            if (file.size > 1024 * 1024 * 2) {
                this.sizeToBig = true
                this.isValidExt = true
                this.fileSize = file.size
                return
            }else{
                this.sizeToBig = false
                this.isValidExt = false
            }


            if(validExt.includes(fileExtention.toLowerCase())){
                this.isValidExt = false
                this.invalidFormat = false
            }else{
                this.isValidExt = true
                this.invalidFormat = true
            }
            
        },
        newMessage(){
            this.dialog = true
            this.editedItem.uploaded_by = this.$store.getters.getName
        },
        getUserTime(time){
            if(time == null){
                return 'Not reported'
            }else{
                return moment.tz(time, "YYYY-MM-DD HH:mm", "UTC")
                .tz(this.$store.getters.getTimezone).format("hh:mm A MM-DD-YYYY")
            }
        },
        editItem(item){
            this.dialogTitle = 'Edit Message'
            this.editedIndex = this.messages.indexOf(item)
            this.editedItem = Object.assign({},item)
            this.isValidExt = false
            this.btnText = 'Save'
            this.dialog = true
        },
        deleteItem(item){
            this.shouldDeleteItem = null
            this.shouldDeleteItem = item
            this.deleteDialog = true
        },
        confirmDelete(){
            const index = this.messages.indexOf(this.shouldDeleteItem)
            axios.delete('message/'+this.shouldDeleteItem.id,{
                params:{
                    token: this.$store.getters.getToken,
                    user_id: this.$store.getters.getUser
                }
                })
                .then(response=>{
                    this.$store.dispatch('setAlert', response.data.message)
                    this.messages.splice(index, 1)
                    this.deleteDialog = false
                })
            
        },
        deleteDialogClose(){
            this.shouldDeleteItem=null
            this.deleteDialog = false
        },
        save(){
            if(!this.$refs.messageForm.validate()) return

            if(this.editedIndex == -1){
                this.isUploading = true
                let file = this.$refs.uploadedFile.files[0]
                let formData = new FormData()
                formData.append('uploaded_by', this.editedItem.uploaded_by)
                formData.append('uploaded_file', file)
                formData.append('stored_file_name', this.editedItem.stored_file_name)
                axios.post('message',formData,{
                    params:{
                        token: this.$store.getters.getToken,
                        user_id: this.$store.getters.getUser
                    }
                })
                .then(response=>{
                  this.$store.dispatch('setAlert', response.data.message)
                  this.isUploading = false
                  this.$refs.messageForm.reset()
                  this.getMessages()
                  this.dialog = false
                })
                .catch(error=>{
                    this.isUploading = false
                    console.log(error.response)
                    this.$store.dispatch('setErrorAlert', 'There is an error uploading file. Please try later.')
                })
            }else{
               let message = {
                   uploaded_by: this.editedItem.uploaded_by,
                   stored_file_name: this.editedItem.stored_file_name
               }

               axios.put('message/'+this.editedItem.id, message,{
                        params: {
                            token: this.$store.getters.getToken,
                            user_id: this.$store.getters.getUser
                        }
                    })
                    .then(response=>{
                        this.$store.dispatch('setAlert', response.data.message)
                        this.getMessages()
                        this.dialog = false
                    })
                    .catch(error=>{
                        this.$store.dispatch('setErrorAlert', error.response.data.message)
                    })           
            }
            
        },
        download(name, newName, ext, filesize){
            window.open(name, '_blank')
        },
        close(){
            this.dialog = false
            setTimeout(()=>{
                this.editedItem = Object.assign({}, this.defaultItem)
                this.editedIndex = -1
                this.selectedSchedule = null
                this.dialogTitle = 'Add Message'
                this.$refs.messageForm.reset()
                this.$refs.messageForm.resetValidation()
            },100)
        },
        getMessages(){
            axios.get('message',
            {
                params:{
                    token:this.$store.getters.getToken,
                    user_id: this.$store.getters.getUser
                }
            })
            .then(response=>{ 
                this.messages = response.data.messages
                this.messages.sort(function(a, b){
                    var keyA = new Date(a.created_at),
                        keyB = new Date(b.created_at)
                    if(keyA > keyB) return -1
                    if(keyA < keyB) return 1
                    return 0
                })

                this.allMessages = this.messages
                if(!this.workingAsAdmin){
                    this.messages = this.messages.filter(message=>{
                        return message.user_id == this.$store.getters.getUser
                    })
                }
            })
        },
        getParmas(item){
            if(item){
                return item.id
            }else{
                return 0
            }
        },
        getUser(){
            return this.$store.getters.getUser
        }
    },
    created(){
        this.getMessages()

        localStorage.setItem('previousUrl', localStorage.getItem('currentUrl'))
        localStorage.setItem('currentUrl', '/admin/messages');
    },
    watch:{
        $route(to, from) {
            if(from.params.id != to.params.id){
               if(!this.$store.getters.getWorkingAs){
                    this.workingAsAdmin = false
                }else{
                    this.workingAsAdmin = true
                }
            }
        }
    },
    computed:{
        headers(){
            return [
                { text: 'Description', value: 'stored_file_name'},
                { text: 'Type', value: 'file_ext'},
                { text: 'Duration', value: 'av_duration'},
                { text: 'File Size', value: 'file_size'},
                { text: 'Uploaded by', value:'uploaded_by'},
                { text: 'Uploaded on', value: 'created_at', width: '15%'},
                { text: 'Action', value: 'actions', width: '17%'}
            ]
        }
    }
}
</script>
<style scoped>
.v-breadcrumbs {
  background-color: #0f4e74;
  padding: 6px 8px;
  color: #fff; 
}
.v-breadcrumbs__item {
  color: #fff;
}

.text-start{
    font-size: 34px !important;
}

.dropzone-custom-content {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

.dropzone-custom-title {
  margin-top: 0;
  color: #00b782;
}

.subtitle {
  color: #314b5f;
}
.v-card__text, .v-card__title {
    padding: 0px 16px;
}
</style>